import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import cn from 'classnames'
import {
    Check,
    Clock3,
    ListMusic,
    MoreHorizontal,
    Pause,
    Play,
    Volume2,
    VolumeX,
    X,
} from 'lucide-react'
import { createPortal } from 'react-dom'
import AudioPlayerCore from '../AudioPlayerCore'
import {
    clearMediaTrackOrigin,
    getMediaPlaybackState,
    isMediaKeyPresentedInDock,
    isMediaOriginHostedInline,
    registerMediaTrack,
    setMediaKeyVolume,
    setMediaPlaybackState,
    setMediaTrackOrigin,
    subscribeToMediaDock,
    subscribeToMediaPlaybackState,
} from 'state/mediaPlaybackCoordinator'
import { toggleTrackInPlaylist } from 'components/shared/MediaDock/mediaDockEvents'
import {
    playlistMenuLabel,
    useMediaKeyInPlaylist,
} from 'components/shared/MediaDock/useMediaKeyInPlaylist'
import { panelSwipeExclusionProps } from 'components/shared/panelSwipeExclusions'
import { combinedMediaAudioScrimIsCompact } from 'lib/combinedMedia'
import useCachedAnswer from 'refactor/hooks/api/useCachedAnswer'
import videoChromeStyles from 'components/shared/video-chrome.module.scss'
import styles from './audio.module.scss'

const AUDIO_PLAYBACK_SPEEDS = [0.75, 1, 1.25, 1.5, 2]

const clampAudioTime = (time, duration) => {
    if (!Number.isFinite(time) || time < 0) return 0
    if (!Number.isFinite(duration) || duration <= 0) return time
    return Math.min(time, duration)
}

export const formatAudioTime = seconds => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00'

    const totalSeconds = Math.floor(seconds)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const remainingSeconds = totalSeconds % 60

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(
            2,
            '0',
        )}`
    }

    return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`
}

// Exported for surfaces that reuse the seek line (the radio card's tuner).
export const buildAudioProgressStyle = progressPercent =>
    /** @type {React.CSSProperties & { '--audio-progress': string }} */ ({
        '--audio-progress': `${progressPercent}%`,
    })

const clampVolume = value => {
    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? Math.max(0, Math.min(1, numericValue)) : 1
}

function AudioAttachmentPlayer({
    src,
    fileName,
    // Playback-coordinator key override. Hosts that register the track themselves
    // (e.g. ordinal renders, which carry artifact metadata) pass their key so the
    // player's dock/volume coordination and its core agree with that registration.
    mediaKey: mediaKeyProp = undefined,
    trackTitle = undefined,
    posterSrc = undefined,
    answerId = undefined,
    questId = undefined,
    audioSourceType,
    knownDuration = undefined,
    theme = 'media',
    compact = false,
    // Collapse volume to its icon and open a vertical flyout above it, the way
    // video chrome and the media dock do. Controls sitting on artwork can't spare
    // the 102px an inline slider takes.
    volumeFlyout = false,
    // A player that lies on someone else's media owns its controls, not the surface under
    // them. Every control stops its own event, so releasing the root's click lets exactly the
    // inert gaps between them reach whatever the player is sitting on — for the combined
    // image+audio card, the answer's openRight handler.
    backgroundClickPassthrough = false,
    className = undefined,
    autoPlay = false,
    isPaused,
    persistPlayback = true,
    playbackKey = undefined,
    resumePlaybackToken,
    onDurationChange,
    onProgressChange,
    onPlaybackError,
    onPlaybackStateChange,
    onManualPlaybackChange,
    // When set, the play button hands the intent to the host instead of
    // starting this element. The radio's "now playing" card uses it to tune
    // the dock into the live station rather than play the file standalone.
    // Returning false falls through to normal local playback.
    onPlayIntent = undefined,
    // Fires with whether the media dock currently owns this track's playback, so a surface
    // hosting this player (the combined-media badge) can mark the tile as live even while
    // the inline element itself is paused.
    onDockOwnedChange = undefined,
    matchingBsvAttachment,
    renderBsvAttachmentStatus,
    showDelete,
    onDelete,
    statusOverlayClassName,
    statusOverlayWithDeleteClassName,
    deleteClassName,
    stopPropagationOnly,
    preventPropagation,
    // A video song on a music card: the track registers as video, and with
    // hosted playback the dock parks its own picture over originRef (the card's
    // artwork) while this strip stays the controls.
    mediaKind = 'audio',
    originRef: originRefProp = undefined,
    onHostedPresentation = undefined,
    // Controls right of the playlist button (a music card's upvalue).
    trailingActions = null,
    // Controls at the left of the scrim's bottom row (a music card's total). The
    // timer moves up beside the seek line to make room.
    leadingActions = null,
}) {
    const audioCoreRef = useRef(null)
    const rootRef = useRef(null)
    const menuButtonRef = useRef(null)
    const volumeControlRef = useRef(null)
    // Restores the pre-mute level when the touch mute toggle is switched back on.
    const lastAudibleVolumeRef = useRef(1)
    const menuRef = useRef(null)
    const rafRef = useRef(null)
    const [isPlaying, setIsPlaying] = useState(false)
    const normalizedKnownDuration =
        Number.isFinite(Number(knownDuration)) && Number(knownDuration) > 0
            ? Number(knownDuration)
            : 0
    const [duration, setDuration] = useState(normalizedKnownDuration)
    const [currentTime, setCurrentTime] = useState(0)
    const [volume, setVolume] = useState(1)
    const [playbackRate, setPlaybackRate] = useState(1)
    const [showMenu, setShowMenu] = useState(false)
    const [showVolumeFlyout, setShowVolumeFlyout] = useState(false)
    const [isDragging, setIsDragging] = useState(false)
    const [isDockOwned, setIsDockOwned] = useState(false)
    const [isScrimCompact, setIsScrimCompact] = useState(false)
    const [menuStyle, setMenuStyle] = useState({
        top: -9999,
        left: -9999,
        opacity: 0,
    })
    const mediaKey =
        mediaKeyProp ||
        (playbackKey || src ? `audio-attachment:${String(playbackKey || src)}` : null)

    // Register this attachment with the media dock so it shows up as a
    // playlist-history track and can keep playing after this player unmounts.
    useEffect(() => {
        if (!persistPlayback || !mediaKey || !src) return undefined

        registerMediaTrack(mediaKey, {
            kind: mediaKind,
            src,
            title: trackTitle || fileName || 'Audio',
            posterSrc: posterSrc || null,
            answerId: answerId || null,
            questId: questId || null,
        })

        // The payload already carries the duration. Seed it so the transport
        // (and the dock's seek bar) read correctly before anything has played
        // — hosted playback has no local element to pull metadata from.
        if (normalizedKnownDuration > 0) {
            const stored = getMediaPlaybackState(mediaKey)
            if (!Number.isFinite(stored?.duration) || stored.duration <= 0) {
                setMediaPlaybackState(mediaKey, { duration: normalizedKnownDuration })
            }
        }

        const originEl = originRefProp?.current || rootRef.current
        setMediaTrackOrigin(mediaKey, originEl)
        return () => clearMediaTrackOrigin(mediaKey, originEl)
    }, [
        mediaKind,
        originRefProp,
        persistPlayback,
        mediaKey,
        src,
        trackTitle,
        fileName,
        posterSrc,
        answerId,
        questId,
        normalizedKnownDuration,
    ])

    useEffect(() => {
        if (!mediaKey) {
            setIsDockOwned(false)
            return undefined
        }

        // "Playing in dock" is about playback that left this post behind. A
        // hosted runtime parked on this very post is still playing here, so it
        // must not offer to bring itself back.
        // The origin is the one this player registered: its own root, or the
        // card's artwork a video song's picture is parked over.
        const updateDockOwnedState = () =>
            setIsDockOwned(
                isMediaKeyPresentedInDock(mediaKey) &&
                    !isMediaOriginHostedInline(mediaKey, originRefProp?.current || rootRef.current),
            )
        updateDockOwnedState()
        return subscribeToMediaDock(updateDockOwnedState)
    }, [mediaKey, originRefProp])

    useEffect(() => {
        onDockOwnedChange?.(isDockOwned)
    }, [isDockOwned, onDockOwnedChange])

    useLayoutEffect(() => {
        if (theme !== 'scrim') {
            setIsScrimCompact(false)
            return undefined
        }

        const el = rootRef.current
        if (!el) return undefined

        const updateCompactState = () => {
            const nextCompact = combinedMediaAudioScrimIsCompact(el.clientWidth)
            setIsScrimCompact(previous => (previous === nextCompact ? previous : nextCompact))
        }

        updateCompactState()
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', updateCompactState)
            return () => window.removeEventListener('resize', updateCompactState)
        }

        const observer = new ResizeObserver(updateCompactState)
        observer.observe(el)
        return () => observer.disconnect()
    }, [theme])

    useEffect(() => {
        if (!mediaKey || !isDockOwned) return undefined

        const syncDockProgress = playbackState => {
            const storedDuration = Number(playbackState?.duration)
            const nextDuration =
                Number.isFinite(storedDuration) && storedDuration > 0 ? storedDuration : duration
            const storedCurrentTime = Number(playbackState?.currentTime)

            if (nextDuration > 0 && Math.abs(nextDuration - duration) > 0.01) {
                setDuration(nextDuration)
                onDurationChange?.(nextDuration)
            }
            if (!Number.isFinite(storedCurrentTime)) return

            const nextCurrentTime = clampAudioTime(storedCurrentTime, nextDuration)
            setCurrentTime(nextCurrentTime)
            if (nextDuration > 0) {
                onProgressChange?.(nextCurrentTime, nextDuration)
            }
        }

        syncDockProgress(getMediaPlaybackState(mediaKey))
        return subscribeToMediaPlaybackState(mediaKey, syncDockProgress)
    }, [duration, isDockOwned, mediaKey, onDurationChange, onProgressChange])

    useEffect(() => {
        if (!mediaKey) return undefined

        const syncVolume = playbackState => {
            if (!Number.isFinite(playbackState?.volume)) return
            const nextVolume = clampVolume(playbackState.volume)
            setVolume(previousVolume =>
                Math.abs(previousVolume - nextVolume) > 0.001 ? nextVolume : previousVolume,
            )
        }

        syncVolume(getMediaPlaybackState(mediaKey))
        return subscribeToMediaPlaybackState(mediaKey, syncVolume)
    }, [mediaKey])

    const stopAudioEvent = useCallback(
        ev => {
            if (stopPropagationOnly) return stopPropagationOnly(ev)
            ev.stopPropagation()
        },
        [stopPropagationOnly],
    )
    const preventAudioEvent = useCallback(
        ev => {
            if (preventPropagation) return preventPropagation(ev)
            ev.preventDefault()
            ev.stopPropagation()
            return false
        },
        [preventPropagation],
    )

    const handlePlaybackSnapshotChange = useCallback(
        snapshot => {
            const nextDuration = Number.isFinite(snapshot?.duration) ? snapshot.duration : 0
            const nextCurrentTime = clampAudioTime(
                Number.isFinite(snapshot?.currentTime) ? snapshot.currentTime : 0,
                nextDuration,
            )

            setIsPlaying(Boolean(snapshot?.isPlaying))
            // Before anything has loaded, the reported duration is 0. Keep the
            // duration the payload already told us rather than blanking the
            // transport.
            setDuration(nextDuration > 0 ? nextDuration : normalizedKnownDuration)
            if (!isDragging) {
                setCurrentTime(nextCurrentTime)
            }
        },
        [isDragging, normalizedKnownDuration],
    )

    const handleTogglePlayback = useCallback(
        async ev => {
            stopAudioEvent(ev)

            // A host that owns the play intent (radio: tune into the station)
            // gets first refusal while this element is not already playing.
            if (onPlayIntent && !isPlaying && onPlayIntent() !== false) return

            try {
                await audioCoreRef.current?.togglePlayback()
            } catch (error) {
                console.warn('Failed to toggle audio playback:', error)
            }
        },
        [isPlaying, onPlayIntent, stopAudioEvent],
    )

    const handleSeek = useCallback(
        ev => {
            stopAudioEvent(ev)

            const nextTime = clampAudioTime(Number(ev.currentTarget.value), duration)
            setCurrentTime(nextTime)
            audioCoreRef.current?.seekTo(nextTime)
        },
        [duration, stopAudioEvent],
    )

    const applyVolume = useCallback(
        nextVolume => {
            audioCoreRef.current?.claimControl?.()
            setVolume(nextVolume)
            if (nextVolume > 0) lastAudibleVolumeRef.current = nextVolume
            if (mediaKey) {
                setMediaKeyVolume(mediaKey, nextVolume)
            }
        },
        [mediaKey],
    )

    const handleVolumeChange = useCallback(
        ev => {
            stopAudioEvent(ev)
            applyVolume(clampVolume(ev.currentTarget.value))
        },
        [applyVolume, stopAudioEvent],
    )

    // The flyout is a hover/focus affordance on pointer devices. On touch the button is a
    // plain mute toggle, because hardware keys own fine-grained volume there.
    const handleVolumeButtonClick = useCallback(
        ev => {
            stopAudioEvent(ev)
            const finePointer =
                typeof window !== 'undefined' &&
                typeof window.matchMedia === 'function' &&
                window.matchMedia('(hover: hover) and (pointer: fine)').matches

            if (finePointer) {
                setShowVolumeFlyout(open => !open)
                return
            }

            applyVolume(volume > 0 ? 0 : clampVolume(lastAudibleVolumeRef.current) || 1)
        },
        [applyVolume, stopAudioEvent, volume],
    )

    const handleToggleMenu = useCallback(
        ev => {
            stopAudioEvent(ev)
            setShowMenu(open => !open)
        },
        [stopAudioEvent],
    )

    const handlePlayHere = useCallback(
        async ev => {
            stopAudioEvent(ev)
            setShowMenu(false)

            try {
                await audioCoreRef.current?.play()
            } catch (error) {
                console.warn('Failed to transfer dock playback to inline audio:', error)
            }
        },
        [stopAudioEvent],
    )

    const handleSetPlaybackRate = useCallback(
        (ev, nextRate) => {
            stopAudioEvent(ev)
            setPlaybackRate(nextRate)
            setShowMenu(false)
        },
        [stopAudioEvent],
    )

    const inPlaylist = useMediaKeyInPlaylist(mediaKey)
    // Listeners other than the poster with this post in their playlist (answer
    // JSON playlist_count). The dock writes the server's count into the cached
    // answer after this viewer's own add or remove, so subscribe to it.
    const cachedAnswer = useCachedAnswer(answerId ? String(answerId) : null)
    const shownPlaylistCount = Number(cachedAnswer?.playlist_count) || 0
    const handleTogglePlaylist = useCallback(
        ev => {
            stopAudioEvent(ev)
            if (!mediaKey) return
            toggleTrackInPlaylist(mediaKey, 'media_player')
        },
        [mediaKey, stopAudioEvent],
    )

    useEffect(() => {
        if (!isPlaying || isDragging) {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current)
            }
            return
        }

        const updateProgress = () => {
            const audio = audioCoreRef.current?.getAudioElement?.()
            if (audio) {
                setCurrentTime(audio.currentTime || 0)
            }
            rafRef.current = requestAnimationFrame(updateProgress)
        }

        rafRef.current = requestAnimationFrame(updateProgress)

        return () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current)
            }
        }
    }, [isPlaying, isDragging])

    useEffect(() => {
        if (!showMenu) return

        const updateMenuPosition = () => {
            if (!menuButtonRef.current || !menuRef.current) return

            const buttonRect = menuButtonRef.current.getBoundingClientRect()
            const menuRect = menuRef.current.getBoundingClientRect()
            const viewportPadding = 12
            const spacing = 8

            let left = buttonRect.right - menuRect.width
            let top = buttonRect.bottom + spacing

            if (left < viewportPadding) {
                left = viewportPadding
            }

            if (left + menuRect.width > window.innerWidth - viewportPadding) {
                left = window.innerWidth - menuRect.width - viewportPadding
            }

            if (top + menuRect.height > window.innerHeight - viewportPadding) {
                top = buttonRect.top - menuRect.height - spacing
            }

            if (top < viewportPadding) {
                top = viewportPadding
            }

            setMenuStyle({
                top,
                left,
                opacity: 1,
            })
        }

        const handleEscape = ev => {
            if (ev.key === 'Escape') {
                setShowMenu(false)
            }
        }

        const rafId = window.requestAnimationFrame(updateMenuPosition)

        window.addEventListener('resize', updateMenuPosition)
        window.addEventListener('scroll', updateMenuPosition, true)
        document.addEventListener('keydown', handleEscape)

        return () => {
            window.cancelAnimationFrame(rafId)
            window.removeEventListener('resize', updateMenuPosition)
            window.removeEventListener('scroll', updateMenuPosition, true)
            document.removeEventListener('keydown', handleEscape)
        }
    }, [showMenu])

    useEffect(() => {
        if (!showVolumeFlyout) return undefined

        const handlePointerDown = ev => {
            if (!volumeControlRef.current?.contains(ev.target)) setShowVolumeFlyout(false)
        }
        const handleEscape = ev => {
            if (ev.key === 'Escape') setShowVolumeFlyout(false)
        }

        document.addEventListener('pointerdown', handlePointerDown, true)
        document.addEventListener('keydown', handleEscape)
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown, true)
            document.removeEventListener('keydown', handleEscape)
        }
    }, [showVolumeFlyout])

    const progressPercent = duration > 0 ? Math.min((currentTime / duration) * 100, 100) : 0
    const isMuted = volume <= 0
    const VolumeIcon = isMuted ? VolumeX : Volume2

    const isScrim = theme === 'scrim'
    const scrimTime = (
        <span className={styles.audioTimeText}>
            {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
        </span>
    )

    const volumeSlider = (
        <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            className={volumeFlyout ? styles.audioVolumeFlyoutRange : styles.audioVolumeSlider}
            style={
                volumeFlyout
                    ? /** @type {React.CSSProperties} */ ({
                          '--audio-volume': `${Math.round(volume * 100)}%`,
                      })
                    : undefined
            }
            onChange={handleVolumeChange}
            onInput={handleVolumeChange}
            onClick={stopAudioEvent}
            onMouseDown={stopAudioEvent}
            onMouseUp={stopAudioEvent}
            onTouchStart={stopAudioEvent}
            onTouchEnd={stopAudioEvent}
            aria-label="Audio volume"
            aria-orientation={volumeFlyout ? 'vertical' : 'horizontal'}
            aria-valuemin={0}
            aria-valuemax={1}
            aria-valuenow={volume}
            aria-valuetext={`${Math.round(volume * 100)}%`}
        />
    )

    // On artwork the timeline is the video chrome's seek bar (thin white line, round handle,
    // tall hit area), so a song and a video over the same kind of picture scrub alike.
    const seekSlider = (
        <input
            type="range"
            min="0"
            max={duration > 0 ? duration : 0}
            step="0.01"
            value={clampAudioTime(currentTime, duration)}
            className={
                isScrim
                    ? cn(videoChromeStyles.seekRange, styles.audioScrimSeekRange)
                    : styles.audioSeekSlider
            }
            style={
                isScrim
                    ? /** @type {React.CSSProperties} */ ({
                          '--video-chrome-progress': `${progressPercent}%`,
                      })
                    : buildAudioProgressStyle(progressPercent)
            }
            onChange={handleSeek}
            onInput={handleSeek}
            onClick={stopAudioEvent}
            onMouseDown={ev => {
                stopAudioEvent(ev)
                setIsDragging(true)
            }}
            onMouseUp={ev => {
                stopAudioEvent(ev)
                setIsDragging(false)
            }}
            onTouchStart={ev => {
                stopAudioEvent(ev)
                setIsDragging(true)
            }}
            onTouchEnd={ev => {
                stopAudioEvent(ev)
                setIsDragging(false)
            }}
            aria-label="Seek audio attachment"
            aria-valuemin={0}
            aria-valuemax={duration > 0 ? duration : 0}
            aria-valuenow={clampAudioTime(currentTime, duration)}
            aria-valuetext={`${formatAudioTime(currentTime)} of ${formatAudioTime(duration)}`}
        />
    )

    const audioActions = (
        <div className={styles.audioActions}>
            <div
                ref={volumeControlRef}
                className={cn(
                    styles.audioVolumeControl,
                    volumeFlyout && styles.audioVolumeControlFlyout,
                )}
                {...panelSwipeExclusionProps}
            >
                {volumeFlyout ? (
                    <button
                        type="button"
                        className={styles.audioVolumeButton}
                        data-story-control-exempt="true"
                        onClick={handleVolumeButtonClick}
                        onMouseDown={stopAudioEvent}
                        onMouseUp={stopAudioEvent}
                        onTouchStart={stopAudioEvent}
                        onTouchEnd={stopAudioEvent}
                        aria-label={isMuted ? 'Unmute audio' : 'Audio volume'}
                        aria-expanded={showVolumeFlyout}
                    >
                        <VolumeIcon className={styles.audioVolumeIcon} aria-hidden="true" />
                    </button>
                ) : (
                    <VolumeIcon className={styles.audioVolumeIcon} aria-hidden="true" />
                )}
                {volumeFlyout ? (
                    <div
                        className={styles.audioVolumeFlyout}
                        data-open={showVolumeFlyout ? 'true' : 'false'}
                    >
                        {volumeSlider}
                    </div>
                ) : (
                    volumeSlider
                )}
            </div>
            <button
                ref={menuButtonRef}
                type="button"
                className={styles.audioMenuButton}
                onClick={handleToggleMenu}
                onMouseDown={stopAudioEvent}
                onMouseUp={stopAudioEvent}
                onTouchStart={stopAudioEvent}
                onTouchEnd={stopAudioEvent}
                aria-label="Audio attachment options"
                aria-expanded={showMenu}
            >
                <MoreHorizontal className={styles.audioMenuButtonIcon} aria-hidden="true" />
            </button>
            {mediaKey && (
                <button
                    type="button"
                    className={cn(
                        styles.audioPlaylistButton,
                        inPlaylist && styles.audioPlaylistButtonActive,
                    )}
                    data-testid="audio-player-playlist-button"
                    onClick={handleTogglePlaylist}
                    onMouseDown={stopAudioEvent}
                    onMouseUp={stopAudioEvent}
                    onTouchStart={stopAudioEvent}
                    onTouchEnd={stopAudioEvent}
                    aria-label={playlistMenuLabel(inPlaylist)}
                    aria-pressed={inPlaylist}
                    title={playlistMenuLabel(inPlaylist)}
                >
                    {inPlaylist ? (
                        <Check className={styles.audioPlaylistButtonIcon} aria-hidden="true" />
                    ) : (
                        <ListMusic className={styles.audioPlaylistButtonIcon} aria-hidden="true" />
                    )}
                    {shownPlaylistCount > 0 && <span>{shownPlaylistCount}</span>}
                </button>
            )}
            {trailingActions}
        </div>
    )

    return (
        <div
            ref={rootRef}
            className={cn(
                styles.customAudioPlayer,
                theme === 'media' && styles.customAudioPlayerMediaTheme,
                theme === 'play' && styles.customAudioPlayerPlayTheme,
                isScrim && styles.customAudioPlayerScrimTheme,
                isScrim && styles.customAudioPlayerScrimLayout,
                isScrim && leadingActions && styles.customAudioPlayerScrimWithLeading,
                isScrimCompact && styles.customAudioPlayerScrimCompact,
                compact && styles.customAudioPlayerCompact,
                className,
            )}
            data-no-modal-interaction
            data-post-embed-navigation-exempt="true"
            // Artwork-backed hosts (the ordinal audio card) place this player as
            // a strip along the bottom. The dock-owned placeholder stands in for
            // the WHOLE embed, so hosts expand the strip to their full box off
            // this flag rather than letting it cover a sliver.
            data-dock-owned={isDockOwned ? 'true' : undefined}
            onClick={backgroundClickPassthrough ? undefined : stopAudioEvent}
            onMouseDown={stopAudioEvent}
            onMouseUp={stopAudioEvent}
            onPointerDown={stopAudioEvent}
            onTouchStart={stopAudioEvent}
            onTouchEnd={stopAudioEvent}
            onDrag={preventAudioEvent}
            onDragStart={preventAudioEvent}
            onDragOver={preventAudioEvent}
            onDragEnter={preventAudioEvent}
            onDragLeave={preventAudioEvent}
            onDrop={preventAudioEvent}
        >
            <AudioPlayerCore
                ref={audioCoreRef}
                as={mediaKind === 'video' ? 'video' : 'audio'}
                originRef={originRefProp || rootRef}
                onHostedPresentation={onHostedPresentation}
                mediaKey={mediaKey}
                src={src}
                className={styles.hiddenAudioElement}
                autoPlay={autoPlay}
                isPaused={isPaused}
                persistPlayback={persistPlayback}
                resumePlaybackToken={resumePlaybackToken}
                volume={volume}
                playbackRate={playbackRate}
                onDurationChange={onDurationChange}
                onProgressChange={onProgressChange}
                onPlaybackError={onPlaybackError}
                onPlaybackStateChange={onPlaybackStateChange}
                onManualPlaybackChange={onManualPlaybackChange}
                onSnapshotChange={handlePlaybackSnapshotChange}
            />

            <button
                type="button"
                className={styles.audioPlayButton}
                data-audio-play-button=""
                data-story-control-exempt="true"
                onClick={handleTogglePlayback}
                onMouseDown={stopAudioEvent}
                onMouseUp={stopAudioEvent}
                onTouchStart={stopAudioEvent}
                onTouchEnd={stopAudioEvent}
                aria-label={isPlaying ? 'Pause audio attachment' : 'Play audio attachment'}
            >
                {isPlaying ? (
                    <Pause className={styles.audioPlayIcon} aria-hidden="true" />
                ) : (
                    <Play
                        className={cn(styles.audioPlayIcon, styles.audioPlayIconPlay)}
                        aria-hidden="true"
                    />
                )}
            </button>

            {isScrim ? (
                <>
                    <div className={styles.audioScrimSeekRow}>
                        {seekSlider}
                        {leadingActions && scrimTime}
                    </div>
                    <div className={styles.audioScrimControlsRow}>
                        {leadingActions ? (
                            <div className={styles.audioScrimLeading}>{leadingActions}</div>
                        ) : (
                            scrimTime
                        )}
                        {audioActions}
                    </div>
                </>
            ) : (
                <>
                    <div className={styles.audioTimelineGroup}>
                        <span className={styles.audioTimeText}>{formatAudioTime(currentTime)}</span>
                        {seekSlider}
                        <span className={styles.audioTimeText}>{formatAudioTime(duration)}</span>
                    </div>
                    {audioActions}
                </>
            )}

            {matchingBsvAttachment && (
                <div
                    className={cn(
                        statusOverlayClassName,
                        showDelete && statusOverlayWithDeleteClassName,
                    )}
                >
                    {renderBsvAttachmentStatus(matchingBsvAttachment)}
                </div>
            )}

            {showDelete && (
                <div className={deleteClassName}>
                    <X className={styles.audioDeleteIcon} onClick={onDelete} aria-hidden="true" />
                </div>
            )}

            {isDockOwned && (
                <div
                    className={styles.playingInDockOverlay}
                    data-dock-placeholder=""
                    role="button"
                    tabIndex={0}
                    aria-label="Play audio here"
                    onClick={handlePlayHere}
                    onKeyDown={event => {
                        if (event.key !== 'Enter' && event.key !== ' ') return
                        handlePlayHere(event)
                    }}
                    onMouseDown={stopAudioEvent}
                    onPointerDown={stopAudioEvent}
                    onTouchStart={stopAudioEvent}
                >
                    <span className={styles.playingInDockLabel}>Playing in dock</span>
                    <button
                        type="button"
                        className={styles.playHereButton}
                        onClick={handlePlayHere}
                        aria-label="Play audio here"
                    >
                        <Play aria-hidden="true" />
                        Play here
                    </button>
                </div>
            )}

            {showMenu &&
                typeof document !== 'undefined' &&
                createPortal(
                    <>
                        <div
                            className={styles.audioMenuBackdrop}
                            onClick={() => setShowMenu(false)}
                            onMouseDown={stopAudioEvent}
                            onTouchStart={stopAudioEvent}
                        />
                        <div
                            ref={menuRef}
                            className={cn(
                                styles.audioMenu,
                                theme === 'media' && styles.audioMenuMediaTheme,
                                theme === 'play' && styles.audioMenuPlayTheme,
                            )}
                            style={menuStyle}
                            data-no-modal-interaction
                            onClick={stopAudioEvent}
                            onMouseDown={stopAudioEvent}
                            onMouseUp={stopAudioEvent}
                            onTouchStart={stopAudioEvent}
                            onTouchEnd={stopAudioEvent}
                        >
                            <div className={styles.audioMenuSectionLabel}>Playback speed</div>

                            <div className={styles.audioSpeedList}>
                                {AUDIO_PLAYBACK_SPEEDS.map(speed => (
                                    <button
                                        key={speed}
                                        type="button"
                                        className={cn(
                                            styles.audioMenuItem,
                                            playbackRate === speed && styles.audioMenuItemActive,
                                        )}
                                        onClick={ev => handleSetPlaybackRate(ev, speed)}
                                    >
                                        <Clock3
                                            className={styles.audioMenuItemIcon}
                                            aria-hidden="true"
                                        />
                                        <span>{speed === 1 ? 'Normal' : `${speed}x`}</span>
                                        {playbackRate === speed && (
                                            <Check
                                                className={styles.audioMenuItemCheck}
                                                aria-hidden="true"
                                            />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </>,
                    document.body,
                )}
        </div>
    )
}

export default AudioAttachmentPlayer
